import { ref, computed, inject, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { CONFIG } from '../config';

const pendingByLocale = new Map();

/**
 * POIs for /location: `initialState.pois` from SSG, or `GET /v1/pois?locale=` + version check.
 */
export function usePointsOfInterest() {
  const initialState = inject('initialState', {});
  const route = useRoute();

  const locale = computed(() => {
    const lang = route.params?.lang;
    if (lang && CONFIG.SUPPORTED_LOCALES.includes(lang)) {
      return lang;
    }
    return initialState?.locale || CONFIG.DEFAULT_LOCALE;
  });

  const embeddedPois = computed(() => {
    if (Array.isArray(initialState?.pois) && initialState.pois.length > 0) {
      return initialState.pois;
    }
    return null;
  });

  const fetchedPois = ref(null);
  const loadingRemote = ref(false);
  const remoteError = ref(null);
  let fetchSeq = 0;

  const items = computed(() => {
    if (fetchedPois.value !== null) {
      return fetchedPois.value;
    }
    if (embeddedPois.value) {
      return embeddedPois.value;
    }
    return [];
  });

  function applyPois(list, versionToken) {
    fetchedPois.value = Array.isArray(list) ? list : [];
    if (initialState && typeof initialState === 'object') {
      initialState.pois = fetchedPois.value;
      if (versionToken) {
        initialState.poisVersion = versionToken;
      }
    }
  }

  async function fetchRemote() {
    if (typeof window === 'undefined') return;
    const loc = locale.value;
    const seq = ++fetchSeq;

    let promise = pendingByLocale.get(loc);
    if (!promise) {
      promise = (async () => {
        const vRes = await fetch(`${CONFIG.API_BASE_URL}/pois/version`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        let remoteVersion = '';
        if (vRes.ok) {
          const vData = await vRes.json();
          remoteVersion = vData.version || '';
        }

        const embeddedVersion = initialState?.poisVersion || '';
        if (
          embeddedPois.value &&
          embeddedPois.value.length > 0 &&
          remoteVersion &&
          remoteVersion === embeddedVersion
        ) {
          return { items: embeddedPois.value, version: remoteVersion };
        }

        const res = await fetch(
          `${CONFIG.API_BASE_URL}/pois?locale=${encodeURIComponent(loc)}&limit=500&offset=0`,
          { headers: { Accept: 'application/json' }, cache: 'no-store' }
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        const items =
          data.success && Array.isArray(data.items) ? data.items : [];
        return {
          items,
          version: remoteVersion
        };
      })().finally(() => {
        pendingByLocale.delete(loc);
      });
      pendingByLocale.set(loc, promise);
    }

    try {
      loadingRemote.value = true;
      remoteError.value = null;
      const { items: list, version } = await promise;
      if (seq === fetchSeq) {
        applyPois(list, version);
      }
    } catch (e) {
      if (seq === fetchSeq) {
        remoteError.value = e.message || 'Failed to load POIs';
      }
    } finally {
      if (seq === fetchSeq) {
        loadingRemote.value = false;
      }
    }
  }

  onMounted(() => {
    const needFetch =
      !embeddedPois.value ||
      embeddedPois.value.length === 0 ||
      !initialState?.poisVersion;
    if (needFetch || import.meta.env.DEV) {
      fetchRemote();
    }
  });

  watch(locale, () => {
    fetchedPois.value = null;
    fetchRemote();
  });

  return {
    locale,
    items,
    isLoading: loadingRemote,
    errorMessage: remoteError,
    refetch: fetchRemote
  };
}
